import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Form as ReactstrapForm,
  Badge,
  Label,
  Input,
} from "reactstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { useGetOrderListMutation } from "../../../redux/services/orderService";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BreadCrumbs from "../../../components/BreadCrumbs";
import "./index.css";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { dateTFormat, formatTimestamp } from "../../../utility/Utility";
import { setSearchRowsPerPage, setSearchSelectedOrderDate } from "../../../redux/orderSlice";
import TableLoadingComponent from "../../../components/TableLoadingComponent";

export interface customItems {
  unitPrice: number;
  displayName: string;
  menuItemId: string;
}

export interface OrderItem {
  unitPrice: number;
  itemType: string;
  quantity: number;
  displayName: string;
  menuItemId: string;
  customItems?: customItems[];
  isExpanded?: boolean;
}

export interface Order {
  id: string;
  restaurantId: string;
  address: string;
  paymentMethod: string;
  displayName: string;
  orderStatus: string;
  orderItems: OrderItem[];
  orderCreatedBy: string;
  orderReference: string;
  cart?: {
    cartId: string;
    cartPersonId: string;
    userId: string;
    displayName: string;
  };
  customer?: {
    id: string;
    displayName: string;
    userId: string;
  };
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  }
}

const OrderListNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [orderList, setOrderList] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDateSelected, setIsDateSelected] = useState(false);

  const [getOrderList, { isLoading }] = useGetOrderListMutation();

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );
  const startDate = useSelector((state: any) => state.order.orderSearchDate);
  const rowsPerPage = useSelector((state: any) => state.order.orderSearchRowsPerPage);

  useEffect(() => {
    if (isDateSelected || restaurantId) {
      fetchOrders();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, startDate, isDateSelected]);

  const fetchOrders = async () => {
    const response = await getOrderList({ orderStatus: "All", restaurantId, startDate , isDeliveryOrder:false });
    if ('data' in response) {
      setOrderList(response?.data?.items ?? []);
      setFilteredOrders(response?.data?.items ?? []);
    }
  };
  const handleViewDetails = (order: Order) => {
    navigate("/orders/search/view", { state: { order } });
  };

  const handleOrderDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = dateTFormat(new Date(e.target.value));
    setIsDateSelected(!!e.target.value);
    dispatch(setSearchSelectedOrderDate(selectedDate));
  };

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    const filteredData = orderList.filter((order) =>
      order.orderReference.toLowerCase().includes(query.toLowerCase())
      || order.displayName.toLowerCase().includes(query.toLowerCase()) || order.cart?.displayName.toLowerCase().includes(query.toLowerCase()) || order.customer?.displayName.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredOrders(filteredData);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setSearchRowsPerPage(newRowsPerPage));
  };

  const columns: TableColumn<Order>[] = [
    {
      name: <h6 className="fw-bold">Order Reference</h6>,
      selector: (row) => row?.orderReference,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Order Creator</h6>,
      selector: (row) => row?.orderCreatedBy,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      selector: (row) => formatTimestamp(row?.createdAt),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Status</h6>,
      cell: (row) => (
        <div>
          <Badge
            color={
              row?.orderStatus === "Pending"
                ? "warning"
                : row?.orderStatus === "AcceptedByCart"
                  ? "secondary"
                  : row?.orderStatus === "ApprovedByRestaurant"
                    ? "info"
                    : row?.orderStatus === "Collecting"
                      ? "danger"
                      : row?.orderStatus === "Preparing"
                        ? "primary"
                        : row?.orderStatus === "ReadyAtKitchen"
                          ? "dark"
                          : row?.orderStatus === "Arrived"
                            ? "secondary"
                            : row?.orderStatus === "InAppCart" ? "dark"
                              : row?.orderStatus === "Completed"
                                ? "success"
                                : "secondary"
            }
            className="px-4 py-2"
          >
            <div style={{ fontSize: 12 }}>
              {row?.orderStatus === "Pending"
                ? "Pending"
                : row?.orderStatus === "AcceptedByCart"
                  ? "Accepted By Cart"
                  : row?.orderStatus === "ApprovedByRestaurant"
                    ? "Approved By Restaurant"
                    : row?.orderStatus === "Collecting"
                      ? "Collecting"
                      : row?.orderStatus === "Preparing"
                        ? "Preparing"
                        : row?.orderStatus === "ReadyAtKitchen"
                          ? "Ready At Kitchen"
                          : row?.orderStatus === "Arrived"
                            ? "Arrived"
                            : row?.orderStatus === "Completed"
                              ? "Completed"
                              : row?.orderStatus === "InAppCart"
                                ? "In App Cart"
                                : "Cancelled"}
            </div>
          </Badge>
        </div>
      ),
      sortable: true,
      center: true,
    },
    {
      name: <h6 className="fw-bold">Action</h6>,
      sortable: true,
      center: true,
      cell: (row: Order) => (
        <>
          <Button onClick={() => handleViewDetails(row)} variant="light">
            <FontAwesomeIcon icon={faEye} color="#FF9A62" />
          </Button>
        </>
      ),
    },
  ];

  return (
    <div>
      <BreadCrumbs hasSubMenus={true} />
      <br />
      <div className="card-header-style-order">
        <ReactstrapForm className="auth-login-form mt-2">
          <Row>
            <Col lg="4">
              <h4>Orders</h4>
            </Col>
            <Col lg="4" className="mb-3">
              <Label>Order Date</Label>
              <Input
                type="date"
                value={startDate.split('T')[0]}
                onChange={handleOrderDateChange}
              />
            </Col>
            <Col lg="4" className="mb-3">
              <Label>Search</Label>
              <Input
                type="text"
                value={searchQuery}
                placeholder="Search by Order"
                onChange={handleSearchQueryChange}
              />
            </Col>
          </Row>
        </ReactstrapForm>
      </div>
      <Card>
        <CardBody>
          {isLoading ? (
            <TableLoadingComponent numColumns={5}/>
          ) : (
            <DataTable
              title=""
              columns={columns}
              data={filteredOrders}
              pagination
              responsive
              highlightOnHover
              paginationPerPage={rowsPerPage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />)}
        </CardBody>
      </Card>
    </div>
  );
};

export default OrderListNew;
